import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { appConfig } from '../config/appConfig';
import { useContent } from '../context/ContentContext';
import { LoadingState } from '../components/LoadingState';
import { ErrorState } from '../components/ErrorState';
import { HighLevelIssueCard } from '../components/HighLevelIssueCard';
import { getTopicPath } from '../lib/contentUtils';
import type { DetailItem, HighLevelIssue } from '../types/content';

const actionCards = [
  {
    title: 'Check Your Registration',
    text: 'Confirm your registration before deadlines, address changes, or polling place updates create problems.',
    to: '/vote',
    cta: 'Verify now',
  },
  {
    title: 'Register to Vote',
    text: 'Find official state registration tools and make sure you are ready before election day.',
    to: '/vote',
    cta: 'Start here',
  },
  {
    title: 'Make a Voting Plan',
    text: 'Decide when, where, and how you will vote, then help someone else do the same.',
    to: '/vote#make-plan',
    cta: 'Build a plan',
  },
  {
    title: 'Learn the Issues',
    text: 'Connect documented actions to healthcare, civil rights, corruption, costs, and public safety.',
    to: '/explore',
    cta: 'Explore issues',
  },
];

const urgencyPoints = [
  'Elections decide who controls healthcare access, civil rights enforcement, public benefits, courts, and oversight.',
  'Small turnout changes can determine whether harmful policies are blocked, reversed, or expanded.',
  'Voting is how documented facts become accountability, not just information.',
];

const rotatingPhrases = [
  'Care for Our Neighbors',
  'Everyone Matters',
  'Fight Corruption',
  'Protect Democracy',
  'Every Vote Counts',
  'Truth Over Fear',
  'Stand up for Justice',
  'Defend Civil Rights',
  'Leadership with Accountability',
  'United for Democracy',
];

const getDayOfYear = () => {
  const today = new Date();
  const startOfYear = new Date(today.getFullYear(), 0, 0);
  return Math.floor((today.getTime() - startOfYear.getTime()) / (1000 * 60 * 60 * 24));
};

const getUniqueIssueItems = (issue: HighLevelIssue) => {
  const seen = new Set<string>();
  const items: DetailItem[] = [];

  for (const specificIssue of issue.specificIssues) {
    for (const item of specificIssue.detailItems) {
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      items.push(item);
    }
  }

  return items;
};

const getDailyEvidenceItems = (issues: HighLevelIssue[], count = 4) => {
  if (issues.length === 0) return [];

  const dayOfYear = getDayOfYear();

  return Array.from({ length: Math.min(count, issues.length) }, (_, index) => {
    const issue = issues[(dayOfYear + index) % issues.length];
    const issueItems = getUniqueIssueItems(issue);
    const item = issueItems[(dayOfYear + index) % issueItems.length];

    return item ? { item, issueTitle: issue.title } : null;
  }).filter((entry): entry is { item: DetailItem; issueTitle: string } => entry !== null);
};

export function HomePage() {
  const { data, loading, error, reload } = useContent();
  const [phraseIndex, setPhraseIndex] = useState(0);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setPhraseIndex((current) => (current + 1) % rotatingPhrases.length);
    }, 2800);

    return () => window.clearInterval(interval);
  }, []);

  if (loading) return <LoadingState />;
  if (error || !data) return <ErrorState message={error ?? 'No data'} onRetry={reload} />;

  const featured = data.highLevelIssues.slice(0, 6);
  const dailyEvidenceItems = getDailyEvidenceItems(data.highLevelIssues);

  return (
    <div className="page home-page">
      <section className="top-action-bar" aria-label="Voter action links">
        <Link to="/vote">Register to Vote</Link>
        <Link to="/vote">Verify Registration</Link>
        <Link to="/vote#early-voting">Find Early Voting</Link>
        <Link to="/vote#make-plan" className="top-action-button">
          Check Your Registration
        </Link>
      </section>

      <section className="hero advocacy-hero">
        <div className="hero-copy">
          <div className="phrase-rotator" aria-live="polite">
            <p className="hero-eyebrow">Democracy on the ballot means</p>
            <h1 key={rotatingPhrases[phraseIndex]} className="animated-phrase">
              {rotatingPhrases[phraseIndex]}
            </h1>
          </div>
          <p className="hero-statement">{appConfig.homeStatement}</p>
          <div className="hero-actions">
            <Link to="/vote" className="btn btn-primary">
              Check Registration
            </Link>
            <Link to="/explore" className="btn btn-ghost">
              Explore the Issues
            </Link>
            <Link to="/vote#make-plan" className="btn btn-ghost">
              Make a Voting Plan
            </Link>
          </div>
        </div>
        <div className="hero-impact-card" aria-label="Why voting matters">
          <span className="impact-kicker">Your vote decides</span>
          <strong>What happens next</strong>
          <p>
            Healthcare. Civil Rights. Accountability. Cost of Living. The outcome of this election will shape them all.
          </p>
        </div>
      </section>

      <section className="voter-action-grid" aria-label="Voter action cards">
        {actionCards.map((card) => (
          <Link key={card.title} to={card.to} className="voter-action-card">
            <span>{card.title}</span>
            <p>{card.text}</p>
            <strong>{card.cta} →</strong>
          </Link>
        ))}
      </section>

      <section className="why-election-matters">
        <div>
          <p className="section-eyebrow">Why this election matters</p>
          <h2>Documented actions have real voting consequences.</h2>
        </div>
        <div className="urgency-points">
          {urgencyPoints.map((point) => (
            <p key={point}>{point}</p>
          ))}
        </div>
      </section>

      <section className="democracy-choice-panel">
        <div>
          <p className="section-eyebrow">Accountability at the ballot box</p>
          <h2>Vote for Democrats to restore checks and balances.</h2>
        </div>
        <div className="democracy-choice-copy">
          <p>
            When corruption, conflicts of interest, and abuses of power are excused or enabled,
            voters have the final check. Republican officials at the state and federal level have
            too often protected party loyalty over independent oversight, leaving fewer guardrails
            against misconduct.
          </p>
          <p>
            This election is an opportunity to choose leaders who will defend democratic
            institutions, protect civil rights, support working families, and hold public officials
            accountable. For voters concerned about corruption and unchecked power, voting for
            Democratic candidates is a practical step toward stronger oversight and responsible
            government.
          </p>
          <div className="hero-actions">
            <Link to="/vote" className="btn btn-primary">
              Get Ready to Vote
            </Link>
            <Link to="/explore" className="btn btn-ghost">
              Review the Record
            </Link>
          </div>
        </div>
      </section>

      <section className="home-sections">
        <div className="section-header-row">
          <div>
            <p className="section-eyebrow">Issue-based persuasion</p>
            <h2>Start with what affects people directly</h2>
          </div>
          <Link to="/explore" className="text-link">
            All {data.stats.highLevelIssueCount} issues →
          </Link>
        </div>
        <div className="section-grid">
          {featured.map((issue, i) => (
            <HighLevelIssueCard key={issue.id} issue={issue} index={i} variant="visual" />
          ))}
        </div>
      </section>

      <section className="evidence-timeline">
        <div className="section-header-row">
          <div>
            <p className="section-eyebrow">Recent federal actions</p>
            <h2>Read the record, then vote with facts.</h2>
          </div>
          <Link to="/explore" className="text-link">
            See all issue categories →
          </Link>
        </div>
        <div className="timeline-list" aria-label="Daily rotating evidence items">
          {dailyEvidenceItems.map(({ item, issueTitle }) => (
            <Link key={item.id} to={getTopicPath(data, item)} className="timeline-item">
              <span className="timeline-tag">{issueTitle}</span>
              <strong>{item.title}</strong>
              <p>{item.body}</p>
              <span className="timeline-read">Read full context →</span>
            </Link>
          ))}
        </div>
      </section>

      <section id="take-action" className="final-cta">
        <p className="section-eyebrow">Take action</p>
        <h2>Don&apos;t sit this election out.</h2>
        <p>
          Millions of people are affected by decisions made by elected officials. Make sure
          your voice is counted, then help someone else get ready to vote.
        </p>
        <div className="hero-actions">
          <Link to="/vote" className="btn btn-primary">
            Verify Registration
          </Link>
          <Link to="/vote#early-voting" className="btn btn-ghost">
            Find Early Voting
          </Link>
          <Link to="/explore" className="btn btn-ghost">
            Explore the Issues
          </Link>
        </div>
      </section>
    </div>
  );
}
